import { CompensationPhase } from "@redotech/cockroachdb-codegen/tables/compensation-phase";
import { advancedFlowSchema_ } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import * as z from "zod";

/**
 * Fetch the path-history for one return's Compensation and Restocking flow
 * executions (RET2-1527). Returns the AdvancedFlow doc(s) for every phase
 * the return will pass through + the ordered list of step ids the worker
 * walked. The viewer renders these on a read-only canvas with the visited
 * steps highlighted.
 *
 * Each phase entry has `revisionSource`:
 *  - `"recorded"` — events for this phase exist; `flow` is the doc at the
 *    `flowRevisionId` the worker actually ran, and `stepIds` are the steps
 *    it walked.
 *  - `"current"` — the phase hasn't fired yet for this return; `flow` is
 *    the team's currently-published doc (the one `getRelevantFlows` would
 *    resolve at runtime), and `stepIds` is empty so nothing highlights.
 *
 * `compensation.phases` includes every configured phase (recorded or not)
 * in `PHASE_ORDER` order; phases with no published doc and no recorded
 * events are skipped. `restocking` is present unless the team has neither
 * recorded restocking events nor a published restocking doc.
 *
 * `hasRevisionDrift` is true when the RECORDED events span more than one
 * `flowRevisionId` — either across phases (merchant republished between
 * two phase executions) or within a single phase (failure-then-retry
 * across a republish). Filled-in `"current"` phases do not contribute to
 * this signal; cross-source comparison is left to the viewer.
 */
const revisionSourceSchema = z.enum(["recorded", "current"]);

const phaseSchema = z.object({
  phase: z.enum(CompensationPhase),
  flow: advancedFlowSchema_,
  stepIds: z.array(z.string()),
  revisionSource: revisionSourceSchema,
});

export const getReturnProcessingFlowPath = {
  name: "getReturnProcessingFlowPath" as const,
  input: z.object({ returnId: z.string() }),
  output: z.object({
    compensation: z.object({ phases: z.array(phaseSchema) }).optional(),
    restocking: z
      .object({
        flow: advancedFlowSchema_,
        stepIds: z.array(z.string()),
        revisionSource: revisionSourceSchema,
      })
      .optional(),
    hasRevisionDrift: z.boolean(),
  }),
};
